/**
 * DocumentFlowPanel — Shared "choose → generate → sign → done" flow used by:
 *   - NegotiationWorkspace (mode='inline'): embedded in an accepted offer's workspace
 *   - DocumentManagement (mode='page'): inside the standalone "New Document" slide-over
 *
 * Identifies a transaction by `offerId` but always shows the property address
 * (passed in via `propertyAddress`) — never the raw offer ID — so users see
 * "123 Main St" instead of "Offer #42".
 *
 * Signing UI uses in-app typed-name flow (ESIGN/UETA).
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import DocumentAPI, { API_BASE_URL } from '../../agents/documentAgent';
import { fetchCertificate, voidDocument } from '../../agents/docusignAgent';
import { useAuth } from '../../agents/authAgent';
import SignerStateCard from '../SignerStateCard/SignerStateCard';
import {
  canUserSignDocument,
  generatableTemplateOptions,
  isBuyerSignedOnly,
} from '../../utils/documentTypes';
import './DocumentFlowPanel.css';

const ROLE_LABELS = {
  buyer: 'the buyer',
  seller: 'the seller',
  buyer_spouse: "the buyer's spouse",
  seller_spouse: "the seller's spouse",
};

const describePendingSigners = (signatures, currentUserId) => {
  const pending = (signatures || []).filter(
    (s) => s.status === 'pending' && s.signer_id !== currentUserId,
  );
  if (pending.length === 0) return null;
  const names = pending.map(
    (s) => s.signer_name || ROLE_LABELS[s.signer_role] || s.signer_role || 'the other party',
  );
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const DEFAULT_TEMPLATE_OPTIONS = [
  { value: 'purchase_agreement',     label: 'Purchase Agreement' },
  { value: 'counter_offer',          label: 'Counter Offer' },
  { value: 'addendum',               label: 'Addendum' },
  { value: 'seller_disclosure',      label: 'Seller Disclosure' },
  { value: 'lead_paint_disclosure',  label: 'Lead Paint Disclosure' },
  { value: 'financing_addendum',     label: 'Financing Addendum' },
];

const STEPS = [
  { key: 'choose',     label: 'Choose' },
  { key: 'generating', label: 'Generate' },
  { key: 'preview',    label: 'Review' },
  { key: 'signing',    label: 'Sign' },
  { key: 'done',       label: 'Done' },
];

const getStepForDocument = (document, userId = null) => {
  const status = (document?.status || '').toLowerCase();
  const hasEnvelope = Boolean(document?.docusign_envelope_id || document?.signatures?.length);

  if (['sent', 'viewed', 'signed', 'completed', 'voided', 'declined'].includes(status) || hasEnvelope) {
    return 'done';
  }
  // Buyer-signed only (the Buyer's Acknowledgment of Receipt): someone who
  // can't sign it — the seller — only ever sees its status.
  if (document && isBuyerSignedOnly(document) && !canUserSignDocument(document, userId)) {
    return 'done';
  }
  if (status === 'generating') {
    return 'generating';
  }
  if (document) {
    return 'preview';
  }
  return 'choose';
};

/**
 * @param {string|null|undefined} offerStatus - The current status of the associated offer.
 *   When provided and not 'accepted', renders a locked or draft-banner state.
 *   Terminal statuses ('rejected', 'withdrawn', 'expired', 'closed') render a fully locked panel.
 *   'closed' (cutthe6.com closed it when the seller deleted the listing) points to
 *   Documents, where the documents already signed stay available.
 *   Pre-acceptance statuses ('pending', 'countered') render with a draft-only banner.
 *   Null/undefined means no status gate is applied (backward-compatible).
 * @param {string|null} [signatureStateHash] - Opaque SHA-256 hex string from the
 *   negotiation status endpoint. When this value changes, the panel refetches
 *   the currently-mounted document to pick up signature-row transitions driven
 *   by webhooks (which don't otherwise flip the offer status or revision count).
 *   The hash is only a refetch trigger — UI state continues to read from
 *   `signature.status` on the document, never from the hash itself.
 */
const DocumentFlowPanel = ({
  offerId,
  offerStatus = null,
  propertyAddress,
  mode = 'inline',          // 'inline' | 'page'
  templateOptions = DEFAULT_TEMPLATE_OPTIONS,
  defaultTemplate = 'purchase_agreement',
  initialDocumentId = null,
  autoStart = false,        // if true, skip choose step and generate immediately
  signatureStateHash = null,
  onClose,
  onComplete,
}) => {
  const TERMINAL_STATUSES = ['rejected', 'withdrawn', 'expired', 'closed'];
  const isTerminalStatus = TERMINAL_STATUSES.includes(offerStatus);
  const isPreAcceptance = offerStatus && offerStatus !== 'accepted' && !isTerminalStatus;

  // Guard autoStart: never auto-generate for terminal offers
  const effectiveAutoStart = isTerminalStatus ? false : autoStart;

  const [step, setStep] = useState(effectiveAutoStart ? 'generating' : 'choose');
  const [templateType, setTemplateType] = useState(defaultTemplate);
  const [doc, setDoc] = useState(null);
  const [typedName, setTypedName] = useState('');
  // QA D2: the name a 422 signature_invalid said to type (a draft has no
  // signer row yet to carry it).
  const [rejectedForName, setRejectedForName] = useState('');
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfPreviewError, setPdfPreviewError] = useState(null);

  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ?? null;
  // Read by the stable load/generate callbacks when they pick a step.
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;

  // Generated with the purchase agreement, never picked (the Buyer's
  // Acknowledgment of Receipt).
  const pickerOptions = useMemo(
    () => generatableTemplateOptions(templateOptions),
    [templateOptions],
  );

  const mySignature = useMemo(() => {
    if (!doc?.signatures || !currentUserId) return null;
    return doc.signatures.find((s) => s.signer_id === currentUserId) || null;
  }, [doc, currentUserId]);

  const alreadySignedByMe = mySignature?.status === 'signed';
  // False for a buyer-signed-only document the viewer has no pending row on
  // (the seller, on the Buyer's Acknowledgment of Receipt).
  const userCanSign = canUserSignDocument(doc, currentUserId);
  const buyerOnlyNotMine = Boolean(doc) && isBuyerSignedOnly(doc) && !mySignature;
  // The typed signature must match this name (as printed on the document,
  // else the name it was sent under, else the account name).
  const expectedName = rejectedForName || mySignature?.expected_name || '';
  const allSigned =
    !!doc?.signatures?.length &&
    doc.signatures.every((s) => s.status === 'signed');
  const waitingFor = useMemo(
    () => describePendingSigners(doc?.signatures, currentUserId),
    [doc, currentUserId],
  );

  const stepIdx = useMemo(() => STEPS.findIndex((s) => s.key === step), [step]);
  const addressLabel = propertyAddress || 'this property';

  const loadDocument = useCallback(async (documentId) => {
    if (!documentId) return null;

    setLoading(true);
    setError(null);
    try {
      const existingDocument = await DocumentAPI.getDocument(documentId);
      setDoc(existingDocument);
      setStep(getStepForDocument(existingDocument, currentUserIdRef.current));
      return existingDocument;
    } catch (err) {
      console.error('DocumentFlowPanel: load existing document failed', err);
      setError(err?.message || 'Failed to load the document envelope. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!offerId) {
      setError('Missing offer reference. Please reopen this panel.');
      return;
    }
    setLoading(true);
    setError(null);
    setStep('generating');
    try {
      const generated = await DocumentAPI.generateDocument(offerId, templateType);
      setDoc(generated);
      setStep(getStepForDocument(generated, currentUserIdRef.current));
    } catch (err) {
      console.error('DocumentFlowPanel: generate failed', err);
      const rawMsg = err?.message || '';
      const isStatusGateError =
        /can only be generated after the offer is accepted/i.test(rawMsg) ||
        /current status/i.test(rawMsg);
      setError(
        isStatusGateError
          ? 'Document generation is not available yet. Offers must be accepted by both parties before documents can be generated.'
          : rawMsg || 'Failed to generate document. Please try again.'
      );
      setStep('choose');
    } finally {
      setLoading(false);
    }
  }, [offerId, templateType]);

  // Auto-generate on mount when effectiveAutoStart=true
  useEffect(() => {
    if (effectiveAutoStart) {
      handleGenerate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialDocumentId) return;
    if (doc?.id === initialDocumentId) {
      setStep(getStepForDocument(doc, currentUserIdRef.current));
      return;
    }
    loadDocument(initialDocumentId);
  }, [initialDocumentId, doc, loadDocument]);

  // Refetch the currently-mounted document whenever the negotiation's
  // signature_state_hash changes. This catches webhook-driven signature
  // transitions (e.g., the other party signs) that don't otherwise flip
  // offer_status / revision_count / comment_count. Skip when:
  //   - no document is mounted yet
  //   - a fetch is already in flight (loading)
  //   - the panel is in an active write step (signing/generating) — refetching
  //     mid-write would clobber form state
  //
  // Why `doc`, `loading`, `step`, and `loadDocument` are in deps even though
  // we only want to react to hash changes: the ref+guard pattern at the top
  // of the effect (`prevSignatureStateHashRef`) is what enforces "run only on
  // hash transition" — when any other dep changes, the early-return
  // `signatureStateHash === prev` exits before doing work. Including them
  // keeps the hook lint-clean and ensures we read fresh values for `doc.id`,
  // `loading`, and `step` on the run that does fire.
  const prevSignatureStateHashRef = useRef(signatureStateHash);
  useEffect(() => {
    const prev = prevSignatureStateHashRef.current;
    prevSignatureStateHashRef.current = signatureStateHash;

    if (!signatureStateHash || signatureStateHash === prev) return;
    if (!doc?.id) return;
    if (loading) return;
    if (step === 'signing' || step === 'generating') return;

    loadDocument(doc.id);
  }, [signatureStateHash, doc, loading, step, loadDocument]);

  // FE2 — DocuSign return refetch.
  //
  // When the user clicks "Sign Now" on a SignerStateCard, DocuSign opens in a
  // new tab and the parent tab gets a synchronous `onSignSessionLaunched`
  // callback. That callback "arms" a one-shot listener: the next time the
  // parent tab gains focus or its document becomes visible (whichever comes
  // first), we refetch the document so the just-signed signature row flips
  // immediately instead of waiting for the next negotiation poll / webhook.
  //
  // Implementation notes:
  //   - `armedSignReloadRef` doubles as the armed flag AND the captured
  //     documentId — non-null means armed, holds the doc id from arm-time
  //     (so we refetch the right document even if `doc.id` changed while the
  //     DocuSign tab was open). A ref is the right tool here: flipping
  //     "armed" shouldn't trigger a re-render.
  //   - Listeners are registered once for the component's lifetime (well,
  //     once per `doc?.id`) and read fresh `loading` / `step` / `loadDocument`
  //     via refs so we don't have to re-attach them on every render.
  //   - Same skip guards as the F4 hash effect above: if a fetch is in flight
  //     or the panel is mid-write, we don't refetch — but we still disarm,
  //     because the user has come back and we shouldn't fire on subsequent
  //     unrelated focus events. They'll re-arm naturally if they click
  //     "Sign Now" again.
  //   - Listener cleanup runs on unmount AND on `doc?.id` change.
  const armedSignReloadRef = useRef(null);
  const loadingRef = useRef(loading);
  const stepRef = useRef(step);
  const loadDocumentRef = useRef(loadDocument);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { loadDocumentRef.current = loadDocument; }, [loadDocument]);

  const handleSignSessionLaunched = useCallback(({ documentId } = {}) => {
    if (!documentId) return;
    armedSignReloadRef.current = documentId;
  }, []);

  useEffect(() => {
    const fire = () => {
      const armedDocId = armedSignReloadRef.current;
      if (!armedDocId) return;
      // One-shot: disarm immediately so neither sibling listener nor a
      // later focus event re-fires the refetch.
      armedSignReloadRef.current = null;

      // Same guard structure as the F4 hash effect — if we're mid-fetch or
      // mid-write, skip the refetch but stay disarmed.
      if (loadingRef.current) return;
      if (stepRef.current === 'signing' || stepRef.current === 'generating') return;

      loadDocumentRef.current?.(armedDocId);
    };

    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        fire();
      }
    };

    window.addEventListener('focus', fire);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', fire);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      // On unmount or doc.id change, drop any pending arm so a stale doc id
      // can't be refetched after this effect tears down.
      armedSignReloadRef.current = null;
    };
  }, [doc?.id]);

  const reset = () => {
    setStep('choose');
    setDoc(null);
    setTypedName('');
    setRejectedForName('');
    setAgreementChecked(false);
    setError(null);
    setPdfPreviewError(null);
  };

  const handleSign = async () => {
    if (!doc || !typedName.trim() || !agreementChecked) return;
    setLoading(true);
    setError(null);
    try {
      await DocumentAPI.signDocument(doc.id, typedName.trim());
      // Refetch to get updated signatures so we can decide whether the
      // document is fully complete or still waiting on the other party.
      let refreshed = doc;
      try {
        refreshed = await DocumentAPI.getDocument(doc.id);
        setDoc(refreshed);
      } catch (refreshErr) {
        console.warn('DocumentFlowPanel: refresh after sign failed', refreshErr);
      }
      setStep('done');
      if (onComplete) onComplete(refreshed);
    } catch (err) {
      console.error('DocumentFlowPanel: sign failed', err);
      const msg = err?.message || '';
      const status = err?.status ?? err?.statusCode;
      const nameMismatch =
        err?.detail?.code === 'signature_invalid' && err.detail.expected_name;
      if (nameMismatch) {
        // 422 "Type your full name as it appears on …: <name>" — stay on the
        // signing step and point the field at the expected name.
        setRejectedForName(err.detail.expected_name);
        setError(msg);
      } else if (/already\s+(signed|declined)/i.test(msg)) {
        // The backend returns 409 "Signature already <status>" when the
        // current user has already signed (or declined). Don't surface that
        // as a scary failure — refresh the document and let the panel render
        // the friendlier "waiting for the other party" state automatically.
        try {
          const refreshed = await DocumentAPI.getDocument(doc.id);
          setDoc(refreshed);
        } catch (refreshErr) {
          console.warn('DocumentFlowPanel: refresh after sign-conflict failed', refreshErr);
        }
        setError(null);
      } else {
        // Everything else must surface on the signing step so the button is
        // never a silent no-op. The backend's opaque 403 "You are not a
        // designated signer" (a draft with no signer row yet) is meaningless
        // to a signer, so translate it to an actionable message; fall back to
        // the backend detail, then a generic message.
        const isNotReady =
          status === 403 || /designated signer/i.test(msg);
        setError(
          isNotReady
            ? "We couldn't record your signature — this document isn't ready to sign yet. "
              + 'Please close this panel and reopen it, or use "Generate & Sign Documents" to start the signing envelope.'
            : msg || 'Failed to apply signature. Please try again.',
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToSigning = () => {
    setError(null);
    setStep(userCanSign ? 'signing' : 'done');
  };

  // Never leave someone who can't sign on the signing step.
  useEffect(() => {
    if (step === 'signing' && doc && !userCanSign) setStep('done');
  }, [step, doc, userCanSign]);

  const documentDownloadUrl = doc ? `${API_BASE_URL}/api/documents/${doc.id}/download` : null;

  // Terminal-status locked panel — render before the normal UI
  if (isTerminalStatus) {
    return (
      <div className="docflow-panel docflow-panel--locked docflow-panel--terminal" role="status">
        <div className="docflow-lock-icon" aria-hidden="true">🔒</div>
        <p className="docflow-lock-title">Documents unavailable</p>
        <p className="docflow-lock-msg">
          This offer was {offerStatus}. Documents cannot be generated.
          {offerStatus === 'expired' ? ' Submit a new offer to restart the process.' : ''}
          {offerStatus === 'closed' ? ' Documents you signed are still in Documents.' : ''}
        </p>
      </div>
    );
  }

  return (
    <div className={`docflow-panel docflow-panel--${mode}`}>
      <div className="docflow-header">
        <div className="docflow-header-text">
          <h3 className="docflow-title">Documents</h3>
          {addressLabel && (
            <p className="docflow-subtitle" title={addressLabel}>
              {addressLabel}
            </p>
          )}
        </div>
        {onClose && (
          <button
            type="button"
            className="docflow-close"
            onClick={onClose}
            aria-label="Close document panel"
          >
            ×
          </button>
        )}
      </div>

      {isPreAcceptance && (
        <div className="docflow-draft-banner" role="note" aria-label="Draft document notice">
          <span className="docflow-draft-banner__icon" aria-hidden="true">ℹ️</span>
          <span>
            <strong>Draft only</strong> — this document has no legal effect until the offer is
            accepted and both parties sign.
          </span>
        </div>
      )}

      <ol className="docflow-steps" aria-label="Document workflow steps">
        {STEPS.map((s, idx) => (
          <li
            key={s.key}
            className={`docflow-step${idx < stepIdx ? ' done' : idx === stepIdx ? ' active' : ''}`}
            aria-current={idx === stepIdx ? 'step' : undefined}
          >
            <span className="docflow-step-dot">{idx < stepIdx ? '✓' : idx + 1}</span>
            <span className="docflow-step-label">{s.label}</span>
          </li>
        ))}
      </ol>

      {error && <div className="docflow-error" role="alert">{error}</div>}

      <div className="docflow-body">
        {step === 'choose' && (
          <div className="docflow-section">
            <p className="docflow-help">
              Select a document type to prepare for{' '}
              <strong>{addressLabel}</strong>. The purchase agreement is required
              for closing.
            </p>
            <label className="docflow-label" htmlFor={`docflow-template-${offerId || 'x'}`}>
              Document type
            </label>
            <select
              id={`docflow-template-${offerId || 'x'}`}
              className="docflow-select"
              value={templateType}
              onChange={(e) => setTemplateType(e.target.value)}
            >
              {pickerOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              type="button"
              className="docflow-btn docflow-btn-primary"
              onClick={handleGenerate}
              disabled={loading || !offerId}
            >
              Generate Document
            </button>
            <p className="docflow-fineprint">
              Federally required disclosures (e.g., Lead-Based Paint for pre-1978 homes)
              are automatically attached.
            </p>
          </div>
        )}

        {step === 'generating' && (
          <div className="docflow-loading">
            <div className="docflow-spinner" aria-hidden="true" />
            <p>Generating your document…</p>
          </div>
        )}

        {step === 'preview' && doc && (
          <div className="docflow-preview">
            <div className="docflow-doc-summary">
              <span className="docflow-doc-icon" aria-hidden="true">📄</span>
              <div>
                <p className="docflow-doc-name">{doc.title}</p>
                <p className="docflow-doc-meta">
                  {doc.property_address || addressLabel}
                </p>
              </div>
            </div>

            <div className="docflow-preview-content">
              <h4>Review your document</h4>
              <p>
                The document is shown below. Review all the details carefully before signing.
              </p>
              {documentDownloadUrl && !pdfPreviewError && (
                <iframe
                  src={documentDownloadUrl}
                  className="docflow-preview-iframe"
                  title={`Preview of ${doc.title}`}
                  onError={() => setPdfPreviewError('PDF preview unavailable')}
                />
              )}
              {pdfPreviewError && (
                <div className="docflow-preview-error">
                  <p>⚠️ {pdfPreviewError}</p>
                  <p className="docflow-fineprint">
                    The PDF preview is temporarily unavailable. You can still proceed to sign the document.
                  </p>
                </div>
              )}
            </div>

            <div className="docflow-preview-actions">
              <button
                type="button"
                className="docflow-btn docflow-btn-secondary"
                onClick={() => {
                  setStep('choose');
                  setPdfPreviewError(null);
                }}
                disabled={loading}
              >
                ← Back
              </button>
              {userCanSign && (
                <button
                  type="button"
                  className="docflow-btn docflow-btn-success"
                  onClick={handleProceedToSigning}
                  disabled={loading}
                >
                  Ready to Sign ✓
                </button>
              )}
            </div>
          </div>
        )}

        {step === 'signing' && doc && userCanSign && (
          <div className="docflow-signing">
            <div className="docflow-doc-summary">
              <span className="docflow-doc-icon" aria-hidden="true">📄</span>
              <div>
                <p className="docflow-doc-name">{doc.title}</p>
                <p className="docflow-doc-meta">
                  {doc.property_address || addressLabel}
                </p>
              </div>
            </div>

            {alreadySignedByMe ? (
              <div className="docflow-waiting">
                <div className="docflow-done-icon" aria-hidden="true">✅</div>
                <h4>You've already signed this document</h4>
                <p>
                  {waitingFor
                    ? <>Waiting for <strong>{waitingFor}</strong> to sign before this document is finalized.</>
                    : 'Waiting for the other party to sign before this document is finalized.'}
                </p>
                <p className="docflow-fineprint">
                  Your signature was recorded on{' '}
                  {mySignature?.signed_at
                    ? new Date(mySignature.signed_at).toLocaleString()
                    : 'an earlier session'}.
                  You'll be notified when the document is fully complete.
                </p>
                <div className="docflow-preview-actions">
                  {documentDownloadUrl && (
                    <a
                      href={documentDownloadUrl}
                      className="docflow-btn docflow-btn-secondary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Document
                    </a>
                  )}
                  {onClose && (
                    <button
                      type="button"
                      className="docflow-btn docflow-btn-outline"
                      onClick={onClose}
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                <p className="docflow-legal">
                  Under the Electronic Signatures in Global and National Commerce
                  Act (ESIGN) and the Uniform Electronic Transactions Act (UETA),
                  your typed name constitutes a legally binding electronic
                  signature equivalent to a handwritten signature.
                </p>
                <label className="docflow-label" htmlFor={`docflow-name-${doc.id}`}>
                  Your full legal name
                </label>
                {expectedName && (
                  <p
                    id={`docflow-name-hint-${doc.id}`}
                    className="docflow-fineprint"
                    data-testid="docflow-name-hint"
                  >
                    Type your name as it appears on the document: <strong>{expectedName}</strong>
                  </p>
                )}
                <input
                  id={`docflow-name-${doc.id}`}
                  type="text"
                  className="docflow-input"
                  placeholder={expectedName || 'e.g. Jane Marie Smith'}
                  aria-describedby={expectedName ? `docflow-name-hint-${doc.id}` : undefined}
                  aria-invalid={Boolean(rejectedForName) || undefined}
                  value={typedName}
                  onChange={(e) => {
                    setTypedName(e.target.value);
                    // A new name is a new attempt: clear the mismatch error.
                    if (rejectedForName) setError(null);
                  }}
                  autoComplete="name"
                  autoFocus
                />
                {typedName.trim() && (
                  <div className="docflow-signature-preview">
                    <span className="docflow-signature-preview-name">
                      {typedName}
                    </span>
                  </div>
                )}
                <label className="docflow-agree">
                  <input
                    type="checkbox"
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                  />
                  <span>
                    I have read this document in full and agree that my typed name
                    constitutes my legal electronic signature for this transaction.
                  </span>
                </label>
                <button
                  type="button"
                  className="docflow-btn docflow-btn-success"
                  onClick={handleSign}
                  disabled={!typedName.trim() || !agreementChecked || loading}
                >
                  {loading ? 'Signing…' : 'Confirm Signature'}
                </button>
              </>
            )}
          </div>
        )}

        {step === 'done' && doc && (
          <div className="docflow-done">
            {allSigned && (
              <div
                className="docflow-executed-banner"
                role="status"
              >
                🎉 Document executed{doc.completed_at ? ` ${new Date(doc.completed_at).toLocaleDateString()}` : ''}
              </div>
            )}

            {/* Document-level state header per design §4.4 */}
            <div className="docflow-state-header">
              <h4 className="docflow-state-header__title">{doc.title}</h4>
              <p className="docflow-state-header__meta">
                {doc.property_address || addressLabel}
                {doc.created_by_name && <> · sent by {doc.created_by_name}</>}
                {doc.sent_at && <> · {new Date(doc.sent_at).toLocaleString()}</>}
              </p>
              <div className="docflow-state-header__actions">
                {documentDownloadUrl && (
                  <a
                    href={documentDownloadUrl}
                    className="docflow-btn docflow-btn-primary"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {/* "executed" is a legal claim about the file, so it has to
                        track the same condition as the executed banner above.
                        A document still out for signature downloads as the
                        unexecuted copy. */}
                    {allSigned ? '↓ Download executed PDF' : '↓ Download unsigned copy'}
                  </a>
                )}
                {doc.esign_provider === 'docusign' && doc.status === 'completed' && (
                  <button
                    type="button"
                    className="docflow-btn docflow-btn-secondary"
                    onClick={async () => {
                      try {
                        await fetchCertificate(doc.id);
                      } catch (err) {
                        setError(err?.message || 'Failed to download certificate');
                      }
                    }}
                  >
                    📄 Certificate of Completion
                  </button>
                )}
                {currentUserId && doc.created_by === currentUserId
                  && (doc.status === 'sent' || doc.status === 'signed') && (
                  <button
                    type="button"
                    className="docflow-btn docflow-btn-outline"
                    onClick={async () => {
                      const reason = window.prompt('Reason for voiding (optional):') || '';
                      try {
                        await voidDocument(doc.id, reason.slice(0, 200));
                        const refreshed = await DocumentAPI.getDocument(doc.id);
                        setDoc(refreshed);
                      } catch (err) {
                        setError(err?.message || 'Failed to void document');
                      }
                    }}
                  >
                    ⊘ Void document
                  </button>
                )}
              </div>
            </div>

            {/* Per-signer state cards */}
            {doc.signatures && doc.signatures.length > 0 && (
              <div className="docflow-signer-cards">
                {doc.signatures.map((sig) => (
                  <SignerStateCard
                    key={sig.id}
                    documentId={doc.id}
                    signature={sig}
                    isSelf={currentUserId != null && sig.signer_id === currentUserId}
                    isSender={currentUserId != null && doc.created_by === currentUserId}
                    onSignSessionLaunched={handleSignSessionLaunched}
                  />
                ))}
              </div>
            )}

            {buyerOnlyNotMine && (
              <p className="docflow-fineprint" role="note" data-testid="docflow-buyer-only-note">
                Only the buyer, and the buyer&apos;s spouse if they are signing
                too, signs this acknowledgment. There is nothing here for you
                to sign.
              </p>
            )}

            {!allSigned && (
              <p className="docflow-fineprint">
                {waitingFor
                  ? <>Waiting for <strong>{waitingFor}</strong> to sign before this document is finalized.</>
                  : buyerOnlyNotMine
                    ? 'Waiting for the buyer to sign.'
                    : alreadySignedByMe
                      // No other signer is pending and I've signed — only a
                      // declined/voided row keeps this from "all signed".
                      ? 'Your signature has been recorded. The other party will be notified.'
                      // I'm the one still pending: don't claim my signature is in.
                      : 'Your signature is still needed to finalize this document.'}
              </p>
            )}

            <div className="docflow-done-actions">
              <button
                type="button"
                className="docflow-btn docflow-btn-secondary"
                onClick={reset}
              >
                Generate Another
              </button>
              {onClose && (
                <button
                  type="button"
                  className="docflow-btn docflow-btn-outline"
                  onClick={onClose}
                >
                  Done
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentFlowPanel;
